import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { subHours } from 'date-fns'
import Loader from '../Loader';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Modal, Avatar, IconButton, Tooltip, Divider, TextField,
  TableSortLabel, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Checkbox, Pagination, LinearProgress, MenuItem, InputAdornment
} from '@mui/material';
import api from '../../../../services/api';
import FeedbackDoCliente from './FeedbackDoCliente';
import { saveAs } from 'file-saver';

const escapeCsvValue = (value) => {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const rowsToCsv = (rows) => {
  if (!rows.length) return '';
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.map(escapeCsvValue).join(','),
    ...rows.map((row) => headers.map((key) => escapeCsvValue(row[key])).join(',')),
  ];
  return `\uFEFF${lines.join('\n')}`;
};

const ClientesTab = () => {
  const [clientes, setClientes] = useState([]);
  const [brindes, setBrindes] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [search, setSearch] = useState('');
  const [nivelSelecionado, setNivelSelecionado] = useState('');
  const [checkinsSelecionado, setCheckinsSelecionado] = useState('');
  const [orderBy, setOrderBy] = useState('ultimaVisita');
  const [order, setOrder] = useState('desc');
  const [niveis, setNiveis] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [checkinsDoCliente, setCheckinsDoCliente] = useState([]);
  const [page, setPage] = useState(1);
  const [camposSelecionados, setCamposSelecionados] = useState({
    nome: true,
    telefone: true,
    data_nascimento: false,
    ultimaVisita: false,
    nivel: false,
  });
  const ROWS_PER_PAGE = 100;
  const DAY_MS = 24 * 60 * 60 * 1000;
  const INACTIVE_DAYS = 30;
  const RECENT_DAYS = 7;
  const MIN_ACTIVE_CHECKINS = 3;
  const initialFetchDoneRef = useRef(false);
  const clientesRequestRef = useRef(0);
  const latestSearchRequestRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const fetchClientes = useCallback(async (searchTerm = '') => {
    const requestId = clientesRequestRef.current + 1;
    clientesRequestRef.current = requestId;

    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);

    const queryString = params.toString();
    const url = queryString ? `/fidelidade/clientes?${queryString}` : '/fidelidade/clientes';
    const response = await api.get(url);

    const clientesData = Array.isArray(response.data) ? response.data : [];

    const parsedClientes = clientesData.map((cliente) => ({
      ...cliente,
      ultimaVisita: cliente.ultima_visita ? new Date(cliente.ultima_visita) : null,
    }));

    if (clientesRequestRef.current !== requestId) return;

    setClientes(parsedClientes);
  }, []);

  const handleOpenModal = async (cliente) => {
    setClienteSelecionado(cliente);
    setModalOpen(true);
    try {
      const res = await api.get(`/fidelidade/checkins/${cliente.telefone}`);
      setCheckinsDoCliente(res.data);
    } catch (err) {
      console.error('Erro ao buscar check-ins do cliente:', err);
      setCheckinsDoCliente([]);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setClienteSelecionado(null);
  };


  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsInitialLoading(true);
        const [resBrindes, resNiveis] = await Promise.all([
          api.get('/fidelidade/brindes-clientes'),
          api.get('/fidelidade/niveis')
        ]);

        setNiveis(resNiveis.data);

        const agrupado = {};
        (resBrindes.data || []).forEach((item) => {
          if (!agrupado[item.cliente_telefone]) agrupado[item.cliente_telefone] = [];
          agrupado[item.cliente_telefone].push(item);
        });

        setBrindes(agrupado);

        await fetchClientes('');
      } catch (err) {
        console.error('Erro ao buscar dados:', err);
        setClientes([]);
        setBrindes({});
      } finally {
        initialFetchDoneRef.current = true;
        setIsInitialLoading(false);
      }
    };

    fetchData();
  }, [fetchClientes]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (!initialFetchDoneRef.current) return;

    const fetchFilteredClientes = async () => {
      const requestToken = Symbol('search-request');
      latestSearchRequestRef.current = requestToken;
      setIsFetching(true);
      try {
        await fetchClientes(debouncedSearch);
      } catch (err) {
        console.error('Erro ao buscar clientes:', err);
      } finally {
        if (latestSearchRequestRef.current === requestToken) {
          setIsFetching(false);
        }
      }
    };

    fetchFilteredClientes();
  }, [debouncedSearch, fetchClientes]);
  
  useEffect(() => {
    setPage(1);
  }, [search, orderBy, order, clientes.length, nivelSelecionado, checkinsSelecionado]);

  const stripDiacritics = (value) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const niveisOrdenados = useMemo(() => (
    [...niveis].sort((a, b) => Number(b.checkins) - Number(a.checkins))
  ), [niveis]);

  const niveisOrdenadosAsc = useMemo(() => (
    [...niveis].sort((a, b) => Number(a.checkins) - Number(b.checkins))
  ), [niveis]);

  const topNivel = niveisOrdenados[0] || null;

  const checkinsDisponiveis = useMemo(() => {
    const valores = new Set();
    clientes.forEach((cliente) => {
      valores.add(Number(cliente.total_checkins || 0));
    });
    return Array.from(valores).sort((a, b) => a - b);
  }, [clientes]);

  const activityThreshold = useMemo(() => {
    const valores = clientes
      .map((cliente) => Number(cliente.total_checkins || 0))
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => a - b);

    if (!valores.length) return MIN_ACTIVE_CHECKINS;

    const index = Math.max(0, Math.floor((valores.length - 1) * 0.75));
    const p75 = valores[index];
    return Math.max(MIN_ACTIVE_CHECKINS, p75);
  }, [clientes]);

  const getNivelAtual = useCallback((qtdCheckins) => {
    if (!niveisOrdenados.length) return null;
    const qtd = Number(qtdCheckins || 0);
    return niveisOrdenados.find(n => qtd >= Number(n.checkins)) || null;
  }, [niveisOrdenados]);

  const filteredClientes = useMemo(() => {
    const rawTerm = (search || '').trim();
    const term = stripDiacritics(rawTerm.toLowerCase());
    const digitTerm = rawTerm.replace(/\D/g, '');
    const hasDigits = digitTerm.length > 0;
    const nivelSelecionadoId = nivelSelecionado;
    const checkinsSelecionadoNumero = checkinsSelecionado === '' ? null : Number(checkinsSelecionado);

    const matchesSearch = (cliente) => {
      if (!rawTerm) return true;

      const nomeNormalizado = stripDiacritics((cliente.nome || '').toLowerCase());
      if (nomeNormalizado.includes(term)) return true;

      const telefoneRaw = cliente.telefone || '';
      if (telefoneRaw.includes(rawTerm)) return true;

      if (hasDigits) {
        const telefoneDigits = telefoneRaw.replace(/\D/g, '');
        if (telefoneDigits.includes(digitTerm)) return true;
      }

      return false;
    };

    const matchesNivel = (cliente) => {
      if (!nivelSelecionadoId) return true;
      const nivelAtual = getNivelAtual(cliente.total_checkins || 0);
      if (!nivelAtual) return false;
      return String(nivelAtual.id) === String(nivelSelecionadoId);
    };

    const matchesCheckins = (cliente) => {
      if (checkinsSelecionadoNumero === null || Number.isNaN(checkinsSelecionadoNumero)) return true;
      return Number(cliente.total_checkins || 0) === checkinsSelecionadoNumero;
    };

    const filtered = clientes.filter(cliente =>
      matchesSearch(cliente) && matchesNivel(cliente) && matchesCheckins(cliente)
    );

    const sorted = filtered.slice().sort((a, b) => {
      const getValue = (cliente) => {
        if (orderBy === 'brindes') return brindes[cliente.telefone]?.length || 0;
        if (orderBy === 'ultimaVisita') return cliente.ultimaVisita ? new Date(cliente.ultimaVisita).getTime() : 0;
        if (orderBy === 'checkins') return cliente.total_checkins || 0;
        return cliente[orderBy];
      };

      const valueA = getValue(a);
      const valueB = getValue(b);

      if (valueA === valueB) return 0;

      if (order === 'asc') {
        return valueA > valueB ? 1 : -1;
      }
      return valueA < valueB ? 1 : -1;
    });

    return sorted;
  }, [clientes, brindes, search, orderBy, order, nivelSelecionado, checkinsSelecionado, getNivelAtual]);

  const totalPages = Math.max(1, Math.ceil(filteredClientes.length / ROWS_PER_PAGE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedClientes = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;
    return filteredClientes.slice(start, start + ROWS_PER_PAGE);
  }, [filteredClientes, page]);

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  if (isInitialLoading) return <Loader />;

  const nowMs = Date.now();

  const getNivelPorCheckins = (qtdCheckins) => {
    if (!niveis.length) return '—';
    const nivel = getNivelAtual(qtdCheckins);
    return nivel ? nivel.nome : '—';
  };

  const formatarTelefone = (telefone) => {
    if (!telefone) return '';
    let clean = telefone.replace(/\D/g, '');
    if (clean.startsWith('55')) clean = clean.slice(2); // remove DDI
    if (clean.length === 10) {
      clean = clean.slice(0, 2) + '9' + clean.slice(2);
    }
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
  };
    
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const exportarContatosCSV = () => {
      const dados = clientes.map((c) => {
        const nivel = getNivelPorCheckins(c.total_checkins || 0);
        const linha = {};
        if (camposSelecionados.nome) linha.Nome = c.nome;
        if (camposSelecionados.telefone) linha.Telefone = '+55' + c.telefone.replace(/\D/g, '');
        if (camposSelecionados.data_nascimento) linha['Data de Nascimento'] = c.data_nascimento;
        if (camposSelecionados.ultimaVisita) {
          linha['Última Visita'] = c.ultimaVisita
            ? new Date(c.ultimaVisita.getTime()).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            : '';
        }
        if (camposSelecionados.nivel) linha['Nível'] = nivel;
        return linha;
      });

      const csv = rowsToCsv(dados);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'contatos.csv');
      setExportDialogOpen(false);
    };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">Lista de Clientes</Typography>
        <Button variant="outlined" color="primary" onClick={() => setExportDialogOpen(true)}>
          Exportar Contatos
        </Button>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
        <TextField
          label="Pesquisar por nome ou telefone"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: '1 1 280px', minWidth: 220 }}
        />
        <TextField
          select
          label="Nível"
          size="small"
          value={nivelSelecionado}
          onChange={(e) => setNivelSelecionado(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {niveisOrdenadosAsc.map((nivel) => (
            <MenuItem key={nivel.id} value={String(nivel.id)}>
              {nivel.nome}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Check-ins"
          size="small"
          value={checkinsSelecionado}
          onChange={(e) => setCheckinsSelecionado(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {checkinsDisponiveis.map((qtd) => (
            <MenuItem key={qtd} value={String(qtd)}>
              {qtd}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {isFetching && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
        <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#FF2D54' }}>
            <TableCell align="center"  sx={{ color: 'white', fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'nome'}
                direction={orderBy === 'nome' ? order : 'asc'}
                onClick={() => handleSort('nome')}
                sx={{ color: 'white' }}
              >
                Nome
              </TableSortLabel>
            </TableCell>
            <TableCell align="center"  sx={{ color: 'white', fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'telefone'}
                direction={orderBy === 'telefone' ? order : 'asc'}
                onClick={() => handleSort('telefone')}
                sx={{ color: 'white' }}
              >
                Telefone
              </TableSortLabel>
            </TableCell>
            <TableCell align="center"  sx={{ color: 'white', fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'checkins'}
                direction={orderBy === 'checkins' ? order : 'asc'}
                onClick={() => handleSort('checkins')}
                sx={{ color: 'white' }}
              >
                Check-ins
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
              Aniversário
            </TableCell>
            <TableCell align="center"  sx={{ color: 'white', fontWeight: 'bold' }}>
              Nível
            </TableCell>
            <TableCell align="center"  sx={{ color: 'white', fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'brindes'}
                direction={orderBy === 'brindes' ? order : 'asc'}
                onClick={() => handleSort('brindes')}
                sx={{ color: 'white' }}
              >
                Brindes Retirados
              </TableSortLabel>
            </TableCell>
            <TableCell align="center"  sx={{ color: 'white', fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'ultimaVisita'}
                direction={orderBy === 'ultimaVisita' ? order : 'asc'}
                onClick={() => handleSort('ultimaVisita')}
                sx={{ color: 'white' }}
              >
                Última Visita
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
              Detalhes
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginatedClientes.map((cliente) => {
            const nivelAtual = getNivelAtual(cliente.total_checkins || 0);
            const nivelNome = nivelAtual ? nivelAtual.nome : 'ƒ?"';
            const ultimaVisitaMs = cliente.ultimaVisita ? cliente.ultimaVisita.getTime() : null;
            const diasDesdeUltimaVisita = ultimaVisitaMs
              ? Math.max(0, Math.floor((nowMs - ultimaVisitaMs) / DAY_MS))
              : null;
            const isInactive = diasDesdeUltimaVisita === null || diasDesdeUltimaVisita >= INACTIVE_DAYS;
            const isVip = topNivel && nivelAtual && String(nivelAtual.id) === String(topNivel.id);
            const isVeryActive = !isInactive
              && !isVip
              && diasDesdeUltimaVisita !== null
              && diasDesdeUltimaVisita <= RECENT_DAYS
              && Number(cliente.total_checkins || 0) >= activityThreshold;

            const statusBadges = [];
            if (isInactive) {
              statusBadges.push({
                key: 'inativo',
                label: 'Inativo',
                color: '#B45309',
                bg: '#FFF7ED',
                tooltip: diasDesdeUltimaVisita === null
                  ? 'Sem última visita registrada'
                  : `Sem visita há ${diasDesdeUltimaVisita} dias`,
              });
            }
            if (isVip) {
              statusBadges.push({
                key: 'vip',
                label: 'VIP',
                color: '#166534',
                bg: '#ECFDF3',
                tooltip: 'Cliente no nível mais alto',
              });
            }
            if (isVeryActive) {
              statusBadges.push({
                key: 'ativo',
                label: 'Muito ativo',
                color: '#1D4ED8',
                bg: '#EFF6FF',
                tooltip: `Visitou nos últimos ${RECENT_DAYS} dias`,
              });
            }

            const accentColor = isInactive
              ? '#B45309'
              : isVip
                ? '#166534'
                : isVeryActive
                  ? '#1D4ED8'
                  : 'transparent';

            return (
              <TableRow
                key={cliente.telefone}
                hover
                onClick={() => handleOpenModal(cliente)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleOpenModal(cliente);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Ver detalhes de ${cliente.nome || 'cliente'}`}
                sx={{
                  cursor: 'pointer',
                  '&:focus-visible': {
                    outline: '2px solid #FF2D54',
                    outlineOffset: -2,
                  }
                }}
              >
                <TableCell align="center">
                  <Box display="flex" alignItems="center">
                    <Avatar
                      sx={{
                        bgcolor: '#252429',
                        color: 'white',
                        mr: 2,
                        boxShadow: accentColor !== 'transparent' ? `0 0 0 2px ${accentColor}` : 'none'
                      }}
                    >
                      {(cliente.nome?.charAt(0).toUpperCase() || '?')}
                    </Avatar>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        sx={{ color: cliente.nome ? 'inherit' : 'gray' }}
                      >
                        {cliente.nome || 'Sem nome'}
                      </Typography>
                      {statusBadges.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                          {statusBadges.map((badge) => (
                            <Tooltip key={badge.key} title={badge.tooltip}>
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 0.75,
                                  py: 0.1,
                                  borderRadius: 1,
                                  fontSize: '0.68rem',
                                  fontWeight: 600,
                                  border: '1px solid',
                                  borderColor: badge.color,
                                  color: badge.color,
                                  bgcolor: badge.bg,
                                  lineHeight: 1.4,
                                }}
                              >
                                {badge.label}
                              </Box>
                            </Tooltip>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="center" >{formatarTelefone(cliente.telefone)}</TableCell>
                <TableCell align="center">
                  {cliente.total_checkins}
                </TableCell>
                <TableCell align="center">
                  {cliente.data_nascimento && cliente.data_nascimento.includes('/')
                    ? cliente.data_nascimento.split('/').slice(0, 2).join('/')
                    : '—'}
                </TableCell>
                <TableCell align="center" >{nivelNome}</TableCell>
                <TableCell align="center" >{brindes[cliente.telefone]?.length || 0}</TableCell>
                <TableCell align="center">
                {cliente.ultimaVisita
                  ? new Date(cliente.ultimaVisita.getTime() + 0  * 60 * 60 * 1000).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'}
                </TableCell>

                <TableCell align="center">
                  <Tooltip title="Ver histórico do cliente">
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: 'text.secondary'
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenModal(cliente);
                        }}
                        aria-label={`Ver detalhes de ${cliente.nome || 'cliente'}`}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                        Ver perfil
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>

        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box mt={3} display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 500,
            maxHeight: '80vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 3,
            p: { xs: 2, sm: 4 },
          }}
        >
          {clienteSelecionado && (
            <>
              <Typography variant="h5" fontWeight="bold" mb={2}>
                👤 {clienteSelecionado.nome || <span style={{ color: '#999' }}>Nome não informado</span>}
              </Typography>

              <Box mb={2}>
                <Typography variant="body1" fontWeight="bold" sx={{ color: '#FF2D54' }}>
                  🏅 Nível Atual: {getNivelPorCheckins(clienteSelecionado.total_checkins)}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box mb={3}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  💬 Último Feedback
                </Typography>
                <FeedbackDoCliente telefone={clienteSelecionado.telefone} />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box mb={3}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  🎁 Brindes Retirados
                </Typography>
                {brindes[clienteSelecionado.telefone]?.length > 0 ? (
                  brindes[clienteSelecionado.telefone].map((item, index) => {
                    const data = item.resgatado_em
                      ? subHours(new Date(item.resgatado_em), 3).toLocaleDateString('pt-BR')
                      : '—';
                    return (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        • {item.brinde_nome} — {data}
                      </Typography>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum brinde retirado.
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  📅 Check-ins Realizados
                </Typography>
                {checkinsDoCliente.length > 0 ? (
                  checkinsDoCliente.map((item, index) => {
                    const data = item.checkin_iniciado_em
                      ? subHours(new Date(item.checkin_iniciado_em), 3).toLocaleDateString('pt-BR')
                      : '—';
                    return (
                      <Typography key={`checkin-${index}`} variant="body2">
                        • {data}
                      </Typography>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum check-in registrado.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </Modal>

      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Selecionar Campos para Exportar</DialogTitle>
        <DialogContent dividers>
          {Object.keys(camposSelecionados).map((campo) => (
            <FormControlLabel
              key={campo}
              control={
                <Checkbox
                  checked={camposSelecionados[campo]}
                  onChange={(e) =>
                    setCamposSelecionados((prev) => ({
                      ...prev,
                      [campo]: e.target.checked
                    }))
                  }
                />
              }
              label={{
                nome: 'Nome',
                telefone: 'Telefone',
                data_nascimento: 'Data de Nascimento',
                ultimaVisita: 'Última Visita',
                nivel: 'Nível',
              }[campo]}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={exportarContatosCSV}>
            Exportar CSV
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientesTab;
